import { debounce } from "lodash";
import React, { useState, useEffect } from "react";
import { ReactComponent as CollapseIcon } from "../../../images/box-collapser.svg";
import { ReactComponent as ArrowIcon } from "../../../images/arrow-small-left.svg";
import Table from "components/elements/Table";
import Select from "components/elements/Select";
import AsyncSpotSelect from "components/elements/AsyncSelect";
import IconInput from "components/elements/IconInput";
import DateInput from "components/elements/DateInput";
import { useOutletContext } from "react-router-dom";
import { CONTENT_TYPE_REVERSE, USER_BUSINESS_ADMIN } from "core/Constants";
import Modal from "components/elements/Modal";
import utility from "core/Utils";
import dataProvider from "core/DataProvider";
import { CLIENT_AGENCY } from "core/Constants";
import { CLIENT_ADVERTISER } from "core/Constants";
import { USER_SUPERADMIN } from "core/Constants";
import { toast } from "react-hot-toast";
import Exporter from "components/elements/Exporter";

const headerMap = {
  channel: "Channel Name",
  adv_date: "Adv Date",
  adv_day: "Adv Day",
  brandsector: "Brand Sector",
  brandcategory: "Brand Category",
  advertisergroup: "Adv Group",
  advertiser: "Advertiser",
  brandname: "Brand Name",
  captions: "Captions",
  breaknumber: "Break",
  positioninbreak: "Position in break",
  countinbreak: "Count in break",
  combinedpositioninbreak: "Combined position in break",
  commercial_program_name: "Commercial program name",
  program_theme: "Program Theme",
  commercial_program_start_time: "Commercial Program Start Time",
  adv_language: "Adv language",
  adv_start_time: "Adv start time",
  adv_end_time: "Adv end time",
  adv_duration: "Adv duration",
};

const exportHeaders = [
  { label: "BRAND", key: "brandname" },
  { label: "CHANNELNAME", key: "channel" },
  { label: "CATEGORY", key: "brandcategory" },
  { label: "PROGRAM DATE", key: "adv_date" },
  { label: "PROGRAM START TIME", key: "commercial_program_start_time" },
  { label: "PROGRAM NAME", key: "commercial_program_name" },
  { label: "AD START TIME", key: "adv_start_time" },
  { label: "AD DURATION", key: "adv_duration" },
  { label: "BREAK NO.", key: "breaknumber" },
  { label: "AD POSITION IN BREAK", key: "positioninbreak" },
  { label: "TOTAL ADS IN BREAK", key: "countinbreak" },
  { label: "AD LANGUAGE", key: "adv_language" },
  { label: "AD THEME", key: "captions" },
  { label: "PREVIOUS AD", key: "previous_spot" },
  { label: "NEXT AD", key: "next_spot" },
  { label: "AD SR.NO.", key: "positioninbreak" },
  { label: "TOTAL ADS IN PROGRAM", key: "totalbreakcountperprogram" },
  { label: "ADVERTISER NAME", key: "advertiser" },
  { label: "SPOT ID", key: "spotid" },
  { label: "DAY OF PRORAM AIRED", key: "adv_day" },
  { label: "REPORTING DATE", key: "report_start_time" },
  { label: "AD END TIME", key: "adv_end_time" },
];

let durations = [];
for (let i = 1; i < 121; i++) {
  durations.push({ value: i, label: `${i} sec` });
}

const AdHocReport = () => {
  const [spotCount, setSpotCount] = useState(0);
  const [channelCount, setChannelCount] = useState(0);
  const [advertiserCount, setAdvertiserCount] = useState(0);
  const [brandCount, setBrandCount] = useState(0);
  const [data, setData] = useState([]);
  const [searchedAdvertiser, setSearchedAdvertiser] = useState("");

  const [commonSegmentCollapsed, setCommonSegmentCollapsed] = useState(false);
  const [extraSegmentCollapsed, setExtraSegmentCollapsed] = useState(true);
  const [formCollapsed, setFormCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const [columns, _] = useState(utility.optionFromObject(headerMap));
  const [selectedColumns, setSelectedColumns] = useState(
    utility.optionFromObject(headerMap)
  );
  const [showColModal, setShowColModal] = useState(false);
  const [search, setSearch] = useState("");
  const [performSearch, setPerformSearch] = useState(false);
  const [exportFormat, setExportFormat] = useState({
    value: "csv",
    label: "CSV",
  });

  const [group, setGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [contentType, setContentType] = useState(null);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [advertiser, setAdvertiser] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [brand, setBrand] = useState(null);
  const [brands, setBrands] = useState([]);
  const [channel, setChannel] = useState(null);
  const [channels, setChannels] = useState([]);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [caption, setCaption] = useState("");
  const [program, setProgram] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [duration, setDuration] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const token = useOutletContext();

  const handleSearchKeyDown = e => {
    if (e.keyCode == 13) {
      setPerformSearch(true);
    }
  };

  // async select functions
  const advertiserLoadOptions = React.useCallback(
    debounce((inputText, callback) => {
      if (inputText.length > 2) {
        advertiserAutoSelect(inputText).then(options => callback(options));
      }
    }, 300),
    []
  );

  // async select functions
  const advertiserAutoSelect = async val => {
    const d = await dataProvider.network.get(
      "min-master/advertisers?ac=" + val
    );
    return d.map(x => {
      return { value: x.id, label: x.name };
    });
  };

  const getGroups = async () => {
    let d = await dataProvider.network.get("min-master/groups");
    if (d) {
      for (let i = 0; i < d.length; i++) {
        d[i] = { value: d[i].id, label: d[i].name };
      }

      utility.sortBy(d, "label");

      setGroups(d);
    } else {
      toast.error("Failed to load groups");
    }
  };

  const getClients = async () => {
    let d = await dataProvider.network.get(
      "min-master/clients?group-id=" + group.value
    );
    if (d) {
      d = d.map(x => {
        return { label: x.name, value: x.id };
      });

      utility.sortBy(d, "label");

      setClients(d);
    } else {
      alert("failed to fetch clients");
    }
  };

  const getCategories = async () => {
    try {
      let cats = await dataProvider.network.post(`masters/brand-category`, {
        advertisers: [advertiser.value],
      });
      cats = cats.map(x => ({
        label: x.brandcategory,
        value: x.brandcategorycode,
      }));

      setCategories(cats);
    } catch (error) {
      console.error(error);
      toast.error("Failed to get categories");
    }
  };

  const getBrands = async () => {
    try {
      let brands = await dataProvider.network.post(`min-master/brand`, {
        advertisers: [parseInt(advertiser.value)],
        categories: category.map(x => parseInt(x.value)),
      });
      brands = brands.map(x => ({ label: x.name, value: x.id }));

      setBrands(brands);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load brands");
    }
  };

  const getChannels = async () => {
    try {
      let channels = await dataProvider.network.get(`min-master/channels`);
      channels = channels.map(x => ({ value: x.id, label: x.name }));

      utility.sortBy(channels, "label");

      setChannels(channels);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch channels");
    }
  };

  const getPrograms = async () => {
    try {
      let channelParams = channel.map(x => `channel=${x.value}`);
      let programs = await dataProvider.network.get(
        `min-master/program?${channelParams.join("&")}`
      );

      programs = programs.map(x => ({ value: x.id, label: x.name }));
      setPrograms(programs);
    } catch (error) {
      console.error(error);
      toast.error("Faile to fetch programs");
    }
  };

  const submit = async e => {
    e.preventDefault();

    try {
      if (!group) {
        throw new Error("Please select a group");
      }
      if (!client) {
        throw new Error("Please select a client");
      }
      if (!periodFrom) {
        throw new Error("Please select from date");
      }
      if (!periodTo) {
        throw new Error("Please select to date");
      }
      if (!channel && !advertiser) {
        throw new Error("Please select a channel or advertiser");
      }
    } catch (error) {
      toast.error(error.message);
      return;
    }

    setLoading(true);

    let data = {
      client_id: client.value,
      period_from: periodFrom,
      period_to: periodTo,
      advertiser: advertiser ? advertiser.value : 0,
      categories: category && category.length ? category.map(x => x.value) : [],
      brands: brand && brand.length ? brand.map(x => x.value) : [],
      channels: channel && channel.length ? channel.map(x => x.value) : [],
      caption: caption,
      program: program && program.value,
      durations: duration && duration.length ? duration.map(x => x.value) : [],
      start_time: utility.getSecondsFromTime(startTime),
      end_time: utility.getSecondsFromTime(endTime),
      content_type: contentType,
    };

    try {
      let records = await dataProvider.network.post("adhoc-report", data);

      setData(records);
      setFormCollapsed(true);

      let spotCount = records.length;
      let channels = [];
      let brands = [];

      for (let i = 0; i < records.length; i++) {
        if (!channels.includes(records[i].channel)) {
          channels.push(records[i].channel);
        }
        if (!brands.includes(records[i].brandname)) {
          brands.push(records[i].brandname);
        }
      }

      setSpotCount(spotCount);
      setChannelCount(channels.length);
      setBrandCount(brands.length);
      setAdvertiserCount(1);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load adhoc report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGroups();
  }, []);

  useEffect(() => {
    if (group && group.value) {
      getClients();
      // getAdvertisers();
      getChannels();
    }
  }, [group]);

  useEffect(() => {
    if (advertiser && advertiser.value) {
      getCategories();
    }
  }, [advertiser]);

  useEffect(() => {
    if (category && category.length) {
      getBrands();
    }
  }, [category]);

  useEffect(() => {
    if (channel && channel.length) {
      getPrograms();
    }
  }, [channel]);

  const permissionCheck = key => {
    let show = false;

    if (key === "advertiser_select") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "advertiser_count") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "brand_count") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "brand_select") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "category_select") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "extra_segment") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (["group_select", "client_select"].includes(key)) {
      if ([USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type)) {
        show = true;
      }
    }

    return show;
  };

  const colModal = (
    <Modal onClose={() => setShowColModal(false)}>
      <h3>Select Columns</h3>
      <div className="full-row" style={{ width: "50vw", marginTop: "2rem" }}>
        <Select
          isMulti
          value={selectedColumns}
          options={columns}
          placeholder="Select columns to show"
          onChange={val => setSelectedColumns(val)}
        />
      </div>
    </Modal>
  );

  let filteredData = [];
  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      let toPush = true;

      if (performSearch && search.length > 2) {
        let searchableString = "";
        let searchString = search.toLowerCase();
        let keys = Object.keys(data[i]);

        for (let j = 0; j < keys.length; j++) {
          if (typeof data[i][keys[j]] == "string") {
            searchableString += data[i][keys[j]].toLowerCase();
          }

          if (typeof data[i][keys[j]] == "number") {
            searchableString += String(data[i][keys[j]]);
          }
        }

        if (!searchableString.includes(searchString)) {
          toPush = false;
        }
      }

      if (toPush) {
        filteredData.push(data[i]);
      }
    }
  }

  return (
    <section id="adhoc-report">
      {showColModal && colModal}
      <div className="grey-box">
        <div className="left">
          <h3>AdHoc Report</h3>
          <h4>Reports &gt; AdHoc Report</h4>
        </div>
        <div className="right">
          {loading && (
            <div className="loader">
              <div className="lds-hourglass"></div>
            </div>
          )}
          <div className="stat">
            <span>{spotCount}</span>
            <span>Spot Count</span>
          </div>
          <div className="stat">
            <span>{channelCount}</span>
            <span>Channels</span>
          </div>
          {permissionCheck("advertiser_count") && (
            <div className="stat">
              <span>{advertiserCount}</span>
              <span>Advertiser</span>
            </div>
          )}
          {permissionCheck("brand_count") && (
            <div className="stat">
              <span>{brandCount}</span>
              <span>Brand</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex mtop">
        <div
          className={formCollapsed ? "form-wrapper collapsed" : "form-wrapper"}
        >
          <div
            className={
              commonSegmentCollapsed ? "form-segment collapsed" : "form-segment"
            }
          >
            <div
              className="segment-head"
              onClick={e => setCommonSegmentCollapsed(!commonSegmentCollapsed)}
            >
              <h6>Common</h6>
              <span>
                <CollapseIcon />
              </span>
            </div>
            <div className="segment-body">
              {permissionCheck("group_select") && (
                <Select
                  options={groups}
                  value={group}
                  onChange={val => setGroup(val)}
                  placeholder="Group"
                />
              )}
              {permissionCheck("client_select") && (
                <Select
                  options={clients}
                  value={client}
                  onChange={val => setClient(val)}
                  placeholder="Client"
                />
              )}
              <DateInput
                value={periodFrom}
                onChange={val => setPeriodFrom(val)}
                placeholder="Period from"
              />
              <DateInput
                value={periodTo}
                onChange={val => setPeriodTo(val)}
                placeholder="Period to"
              />
              {permissionCheck("advertiser_select") && (
                <AsyncSpotSelect
                  placeholder="Advertiser"
                  loadOptions={advertiserLoadOptions}
                  onChange={val => {
                    setAdvertiser(val);
                  }}
                  onInputChange={val => {
                    setSearchedAdvertiser(val);
                  }}
                  noOptionsMessage={() => "Type to search (min 3 characters)"}
                />
              )}
              <Select
                isMulti
                options={channels}
                value={channel}
                onChange={val => setChannel(val)}
                placeholder="Channel"
              />
            </div>
          </div>
          {permissionCheck("extra_segment") && (
            <div
              className={
                extraSegmentCollapsed
                  ? "form-segment collapsed"
                  : "form-segment"
              }
            >
              <div
                className="segment-head"
                onClick={e => setExtraSegmentCollapsed(!extraSegmentCollapsed)}
              >
                <h6>Extras</h6>
                <span>
                  <CollapseIcon />
                </span>
              </div>
              <div className="segment-body">
                {permissionCheck("category_select") && (
                  <Select
                    isMulti
                    options={categories}
                    value={category}
                    onChange={val => setCategory(val)}
                    placeholder="Category"
                  />
                )}
                {permissionCheck("brand_select") && (
                  <Select
                    isMulti
                    options={brands}
                    value={brand}
                    onChange={val => setBrand(val)}
                    placeholder="Brand"
                  />
                )}
                <Select
                  isMulti
                  options={[
                    {
                      value: CONTENT_TYPE_REVERSE.Commercial,
                      label: "Commercial",
                    },
                    { value: CONTENT_TYPE_REVERSE.Promo, label: "Promo" },
                    { value: CONTENT_TYPE_REVERSE.Ica, label: "ICA" },
                  ]}
                  value={contentType}
                  onChange={val => setContentType(val)}
                  placeholder="Content Type"
                />
                <input
                  type="text"
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  placeholder="Caption"
                />
                <Select
                  options={programs}
                  value={program}
                  onChange={val => setProgram(val)}
                  placeholder="Program"
                />
                <Select
                  isMulti
                  options={durations}
                  value={duration}
                  onChange={val => setDuration(val)}
                  placeholder="Duration"
                />
                <input
                  className="monitoring-start-time"
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  placeholder="Start Time"
                />
                <input
                  className="monitoring-end-time"
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  placeholder="End Time"
                />
              </div>
            </div>
          )}
          <button onClick={submit}>Submit</button>
          <div
            className="collapser"
            onClick={e => setFormCollapsed(!formCollapsed)}
          >
            <ArrowIcon />
          </div>
        </div>
        <div className="results-wrapper flex-full">
          <div className="head flex flex-v-center">
            <h3>Results</h3>
            <div className="right">
              <div className="flex flex-v-center">
                <div className="el">
                  <input
                    type="text"
                    readOnly
                    placeholder="Select Columns"
                    onClick={e => {
                      e.preventDefault();
                      setShowColModal(true);
                    }}
                  />
                </div>
                <div className="el">
                  <IconInput
                    value={search}
                    onKeyDown={handleSearchKeyDown}
                    onChange={e => {
                      setPerformSearch(false);
                      setSearch(e.target.value);
                    }}
                    placeholder="Search"
                    icon="search"
                  />
                </div>
                <div className="el">
                  <Select
                    value={exportFormat}
                    onChange={val => {
                      setExportFormat(val);
                    }}
                    options={[
                      { value: "csv", label: "CSV" },
                      { value: "xlsx", label: "XLSX" },
                    ]}
                    placeholder="Format"
                  />
                </div>
                <Exporter
                  format={exportFormat.value}
                  data={data}
                  headers={exportHeaders}
                  filename={`${utility.formatDate(
                    new Date()
                  )}_${periodFrom}_${periodTo}_${utility.getRandomNumbers(
                    10000,
                    99999
                  )}.${exportFormat.value}`}
                />
              </div>
            </div>
          </div>
          <div className="results">
            <Table
              headers={utility.objectFromOptions(selectedColumns)}
              data={filteredData}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdHocReport;
